import {
  Badge,
  Box,
  Flex,
  Loader,
  Menu,
  Skeleton,
  Text,
  Stack,
} from "@mantine/core";
import Tooltip from "components/Tooltip/Tooltip";
import React, { useState } from "react";
import { TbTargetArrow } from "react-icons/tb";
import { HiPencil } from "react-icons/hi";
import useStrategicGoalSubscription from "hooks/useStrategicGoalSubscription";
import { useUser } from "stores/useUser";
import { IconInfoCircle } from "@tabler/icons-react";
import { buildGoalTooltip } from "utils/strategicGoals";
import GoalMenuDropdown from "./GoalMenuDropdown";
const StrategicGoal = ({
  goals,
  selectedEvent,
  submitGoal,
  refreshMetaData = () => {},
  source = "workspace",
}) => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [updating, setUpdating] = useState(false);
  const { user } = useUser();
  const isAdmin = user?.roles?.includes("admin");
  const organizerDomain = selectedEvent?.organizer?.email
    ? selectedEvent.organizer.email.split("@")[1]
    : undefined;
  const userDomain = user?.email ? user.email.split("@")[1] : undefined;
  const isInternalOrganizer =
    organizerDomain && userDomain && organizerDomain === userDomain;
  // Live: the post-meeting pipeline can assign a goal while this is on screen.
  const goalDocId =
    selectedEvent?.recurringEventId || selectedEvent?.eventId || null;
  const { data: eventGoal, isLoading: isLoadingGoal } =
    useStrategicGoalSubscription(goalDocId);

  // Update local state when event goal data changes
  React.useEffect(() => {
    if (eventGoal !== undefined) {
      setSelectedGoal(eventGoal);
    }
  }, [eventGoal]);
  const handleGoalUpdate = async (goal) => {
    if (goal !== selectedGoal) {
      setSelectedGoal(goal);
      try {
        setUpdating(true);
        await submitGoal(goal, selectedEvent?.eventId);
        refreshMetaData();
      } catch (err) {
        throw err;
      } finally {
        setUpdating(false);
      }
    }
  };

  const canEdit =
    (selectedEvent?.organizer?.self || (isAdmin && isInternalOrganizer)) &&
    !updating;
  const goalName = selectedGoal?.goal;
  const { label: tooltipLabel, isSentence } = buildGoalTooltip({
    goal: goalName,
    reason: selectedGoal?.reason,
    canEdit,
  });

  if (isLoadingGoal) return <Skeleton height={24} width={100} radius="xl" />;

  return (
    <Flex align={"center"} gap={".3rem"}>
      {updating ? (
        <Badge variant="light" color="blue" sx={{ display: "flex" }}>
          <Text inline>Updating...</Text>
        </Badge>
      ) : (
        <Menu
          shadow="md"
          disabled={!canEdit}
          key={selectedEvent?.eventId}
          withinPortal
        >
          <Menu.Target>
            <Stack spacing={"0.5rem"} align="center">
              <Badge
                size="lg"
                variant={canEdit || selectedGoal?.color ? "filled" : "light"}
                sx={(t) => ({
                  // Grows with the goal name — org goals are phrases, not
                  // labels. The old fixed width forced an 8-character cut.
                  minWidth: source === "workspace" ? "8rem" : "7rem",
                  maxWidth: source === "workspace" ? "20rem" : "14rem",
                  cursor: "pointer",
                  backgroundColor:
                    canEdit || selectedGoal?.color
                      ? t.colors[selectedGoal?.color]?.[5]
                      : t.colors.gray[5],
                  border:
                    canEdit &&
                    `1px solid ${t.colors?.[selectedGoal?.color]?.[6]}`,
                  color: "white",
                })}
              >
                <Tooltip
                  label={tooltipLabel}
                  // `multiline` alone only permits wrapping; without a cap the
                  // tooltip grows to the width of its text, and an AI reason is
                  // long enough to span the viewport and clip at both ends.
                  // maxWidth rather than Mantine's `width`, which is fixed and
                  // would pad a two-word goal name out to the same box.
                  sx={{
                    textTransform: isSentence ? "none" : "lowercase",
                    maxWidth: "22rem",
                    whiteSpace: "normal",
                    lineHeight: 1.45,
                    textAlign: "left",
                  }}
                >
                  <Flex
                    align={"center"}
                    justify={"center"}
                    gap={".5rem"}
                    sx={{ minWidth: 0 }}
                  >
                    {source === "workspace" && (
                      <TbTargetArrow size={15} style={{ flexShrink: 0 }} />
                    )}
                    <Text
                      sx={{
                        textTransform: "capitalize",
                        // Only bites past maxWidth; the tooltip has the full name.
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                      fw={"normal"}
                    >
                      {goalName || "No goal"}
                    </Text>
                    {canEdit && (
                      <Box
                        miw={18}
                        p={0}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <HiPencil size={15} />
                      </Box>
                    )}
                  </Flex>
                </Tooltip>
              </Badge>
              {selectedGoal?.updateByAdmin && (
                <Flex align={"center"} gap={".3rem"}>
                  <IconInfoCircle size={14} />
                  <Text inline size="xs" c="dimmed">
                    Updated by admin
                  </Text>
                </Flex>
              )}
            </Stack>
          </Menu.Target>

          <GoalMenuDropdown goals={goals} onSelect={handleGoalUpdate} />
        </Menu>
      )}

      {updating && <Loader size={16} />}
    </Flex>
  );
};

export default StrategicGoal;
